import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Spinner } from "../components/ui/shadcn-io/spinner";
import {
  DollarSign,
  TrendingUp,
  TrendingDown,
  Wallet,
  XCircle,
  IndianRupee,
} from "lucide-react";
import { Badge } from "../components/ui/badge";

interface SalaryData {
  _id: string;
  employeeId: string;
  basic: number;
  hra: number;
  otherAllowances: number;
  deductions: {
    pf: number;
    esi: number;
  };
  total: number;
  createdAt: string;
  updatedAt: string;
}

export default function EmployeeSalary() {
  const [salaryData, setSalaryData] = useState<SalaryData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    fetchSalaryData();
  }, []);

  const fetchSalaryData = async () => {
    try {
      setIsLoading(true);
      setError("");

      const token = localStorage.getItem("authToken");
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}api/employees/me/salary`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setSalaryData(response.data);
    } catch (error: any) {
      console.error("Error fetching salary data:", error);
      if (error.response?.status === 404) {
        setError(
          "Salary information not yet configured. Please contact admin."
        );
      } else {
        setError(
          error.response?.data?.message || "Failed to fetch salary details"
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(amount);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center gap-2">
          <Spinner variant="ellipsis" size={40} />
          <p className="text-sm text-muted-foreground">
            Loading your salary details...
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold">My Salary Details</h1>
            <p className="text-muted-foreground mt-1">
              View your salary breakdown and components
            </p>
          </div>
        </div>

        <Card>
          <CardContent className="py-12">
            <div className="text-center">
              <div className="mx-auto w-16 h-16 bg-muted rounded-full flex items-center justify-center mb-4">
                <DollarSign className="h-8 w-8 text-muted-foreground" />
              </div>
              <h3 className="text-lg font-medium mb-2">
                Salary information not yet configured.
              </h3>
              <p className="text-muted-foreground mb-4">
                You don't have any your salary details setup.
              </p>
              <p className="text-sm text-muted-foreground">
                Please contact admin.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!salaryData) {
    return null;
  }

  const grossEarnings =
    salaryData.basic + salaryData.hra + salaryData.otherAllowances;
  const totalDeductions = salaryData.deductions.pf + salaryData.deductions.esi;
  const netSalary = grossEarnings - totalDeductions;

  return (
    <div className="container mx-auto px-6 py-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold">My Salary Details</h1>
          <p className="text-muted-foreground mt-1">
            Your current salary structure and breakdown
          </p>
        </div>
        <Badge className="bg-green-100 text-green-800 border-green-200">
          Active
        </Badge>
      </div>

      {/* Net Salary Card */}
      <Card className="mb-6 border-2 border-primary">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground mb-1">
                Monthly Net Salary
              </p>
              <p className="text-4xl font-bold text-primary">
                {formatCurrency(netSalary)}
              </p>
              <p className="text-xs text-muted-foreground mt-2">
                Last updated:{" "}
                {new Date(salaryData.updatedAt).toLocaleDateString("en-IN", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </p>
            </div>
            <div className="p-4 bg-primary/10 rounded-full">
              <Wallet className="h-12 w-12 text-primary" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Earnings Section */}
      <div className="mb-6">
        <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
          <TrendingUp className="h-5 w-5 text-green-600" />
          Earnings
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <IndianRupee className="h-4 w-4" />
                Basic Salary
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold">
                {formatCurrency(salaryData.basic)}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                {((salaryData.basic / grossEarnings) * 100).toFixed(1)}% of
                gross
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <IndianRupee className="h-4 w-4" />
                House Rent Allowance (HRA)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold">
                {formatCurrency(salaryData.hra)}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                {((salaryData.hra / grossEarnings) * 100).toFixed(1)}% of gross
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <IndianRupee className="h-4 w-4" />
                Other Allowances
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold">
                {formatCurrency(salaryData.otherAllowances)}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                {((salaryData.otherAllowances / grossEarnings) * 100).toFixed(
                  1
                )}
                % of gross
              </p>
            </CardContent>
          </Card>
        </div>

        <Card className="mt-4 bg-green-50 border-green-200">
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <p className="font-semibold text-green-800">
                Total Gross Earnings
              </p>
              <p className="text-2xl font-bold text-green-800">
                {formatCurrency(grossEarnings)}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Deductions Section */}
      <div className="mb-6">
        <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
          <TrendingDown className="h-5 w-5 text-red-600" />
          Deductions
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <IndianRupee className="h-4 w-4" />
                Provident Fund (PF)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold text-red-600">
                {formatCurrency(salaryData.deductions.pf)}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Retirement savings contribution
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <IndianRupee className="h-4 w-4" />
                Employee State Insurance (ESI)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold text-red-600">
                {formatCurrency(salaryData.deductions.esi)}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Health insurance contribution
              </p>
            </CardContent>
          </Card>
        </div>

        <Card className="mt-4 bg-red-50 border-red-200">
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <p className="font-semibold text-red-800">Total Deductions</p>
              <p className="text-2xl font-bold text-red-800">
                {formatCurrency(totalDeductions)}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Summary Card */}
      <Card className="border-2 border-blue-200 bg-blue-50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <DollarSign className="h-5 w-5" />
            Salary Summary
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="flex items-center justify-between py-2 border-b">
              <p className="text-sm font-medium">Gross Earnings</p>
              <p className="text-sm font-bold text-green-600">
                {formatCurrency(grossEarnings)}
              </p>
            </div>
            <div className="flex items-center justify-between py-2 border-b">
              <p className="text-sm font-medium">Total Deductions</p>
              <p className="text-sm font-bold text-red-600">
                - {formatCurrency(totalDeductions)}
              </p>
            </div>
            <div className="flex items-center justify-between py-3 bg-white rounded-lg px-4">
              <p className="font-semibold text-lg">Net Salary (Take Home)</p>
              <p className="text-2xl font-bold text-blue-600">
                {formatCurrency(netSalary)}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
